// Nurse3Page.tsx
import React, { useMemo, useState, useEffect, JSX } from "react";
import { useOutletContext } from "react-router-dom";
import type { OutletCtx } from "../../../components/Center Admin/layout/AppLayout";
import "../../../center admin css/centerAdmin.css";

import Nurse3ActivePatientCard from "../../../components/Center Admin/Reuse/Nurse3ActivePatientCard";

// Import all potentially necessary form components to make the page fully dynamic
import { AnthropometricMeasurementForm } from "../../../components/Center Admin/forms/AnthropometricMeasurementForm";
import { PhysicalExaminationForm } from "../../../components/Center Admin/forms/PhysicalExaminationForm";
import { ADMinorSymptomsExaminationForm } from "../../../components/Center Admin/forms/ADMinorSymptomsExaminationForm";
import { BUSampleForm } from "../../../components/Center Admin/forms/BUSampleForm";
import BnuSampleForm from "../../../components/Center Admin/forms/sentToLab";
import LabReportForm from "../../../components/Center Admin/forms/LabReportForm";
import { VisionScreeningForm } from "../../../components/Center Admin/forms/VisionScreeningForm";
import { OralImagingForm } from "../../../components/Center Admin/forms/OralImagingForm";
import { VitalsExaminationForm } from "../../../components/Center Admin/forms/VitalsExaminationForm";
import { SalivaSampleForm } from "../../../components/Center Admin/forms/SalivaSampleForm";
import { CariogramInputForm } from "../../../components/Center Admin/forms/CariogramInputForm";
import AudiologyScreeningForm from "../../../components/Center Admin/forms/AudiologyScreeningForm";

/** ---------- helpers ---------- */
const normalize = (s?: string) =>
  (s ?? "").toString().trim().toUpperCase().replace(/\s+/g, " ");

/**
 * Registry: maps a BACKEND key/name to its corresponding React component.
 * This is the central control point for which forms can be rendered.
 */

type BackendForm = {
  _id?: string;
  name?: string;
  key?: string;
  isActive?: boolean;
};

type AllowedForm = {
  id: string;
  label: string;
  render: (emrId: string) => JSX.Element;
};

/** 🔁 Robust user loader: tries multiple keys + both storages */
function loadUserFromStorage(): any | null {
  const keys = [
    "currentUser",
    "CurrrentResponse",
    "CurrentResponse",
    "backendResponse",
  ];
  const stores: Storage[] = [sessionStorage, localStorage];

  const read = (store: Storage, key: string) => {
    try {
      const raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  for (const s of stores) {
    for (const k of keys) {
      const v = read(s, k);
      if (v && typeof v === "object") return v;
    }
  }
  return null;
}

export const Nurse3Page: React.FC = () => {
  const { activePatient } = useOutletContext<any>();

  const emrId = activePatient?.child?.childId || "";

  useEffect(() => {
    if (activePatient?.sessionId) {
      sessionStorage.setItem("workflowSessionId", activePatient.sessionId);
    }
  }, [activePatient]);

  const canCollectBUSample =
    activePatient?.canCollectBUSample ??
    activePatient?.meta?.canCollectBUSample ??
    false;

  console.log(
    "activePatient:",
    activePatient,
    "canCollectBUSample:",
    canCollectBUSample,
  );

  const [loadingForms, setLoadingForms] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [visibleForms, setVisibleForms] = useState<AllowedForm[]>([]);
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [formStatus, setFormStatus] = useState({
    buSample: false,
  });
  const [isStarted, setIsStarted] = useState(false);

  const updateFormStatus = (key: string, value: boolean) => {
    setFormStatus((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const FORM_COMPONENTS: Record<string, (emrId: string) => JSX.Element> = {
    // Anthropometry
    ANTHROPOMETRY: (emrId) => <AnthropometricMeasurementForm emrId={emrId} />,
    "ANTHROPOMETRIC MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm emrId={emrId} />
    ),
    "ANTHROPOMETRY MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm emrId={emrId} />
    ),

    // Physical Examination
    "PHYSICAL EXAMINATION": (emrId) => (
      <PhysicalExaminationForm emrId={emrId} />
    ),

    // AD Minor Symptoms
    "ADMINOR SYSPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "ADMINOR SYMPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "AD MINOR SYMPTOMS": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),

    "B&U SAMPLE": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),

    "BLOOD AND URIN SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),

    "BLOOD AND URINE SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),

    "BLOOD SAMPLE": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),

    // Extra forms
    "BLOOD SAMPLE SENT TO LAB": (emrId) => <BnuSampleForm emrId={emrId} />,
    "LAB REPORT": (emrId) => <LabReportForm emrId={emrId} />,
    "VISION SCREENING": (emrId) => <VisionScreeningForm emrId={emrId} />,
    "ORAL IMAGING": (emrId) => <OralImagingForm emrId={emrId} />,
    "VITALS EXAMINATION": (emrId) => <VitalsExaminationForm emrId={emrId} />,
    "CARIOGRAM SALAIVA SAMPLE": (emrId) => <SalivaSampleForm emrId={emrId} />,
    // "GROWTH CHART ENTRY": (emrId) => <GrowthChartEntry emrId={emrId} />,
    "CARIOGRAM INPUT": (emrId) => <CariogramInputForm emrId={emrId} />,
    "AUDIOLOGY SCREENING": (emrId) => <AudiologyScreeningForm emrId={emrId} />,
  };

  useEffect(() => {
    if (!activePatient) {
      setIsStarted(false);
      setActiveIdx(0);
    }
  }, [activePatient]);

  useEffect(() => {
    setLoadingForms(true);
    setLoadError(null);

    try {
      const user = loadUserFromStorage();
      if (!user) {
        throw new Error("User data not found in session. Please log in again.");
      }

      const fromRole: BackendForm[] =
        user?.roleAssignments?.[0]?.roleId?.formIds ?? [];
      const fallback: BackendForm[] = user?.forms ?? [];
      const backendForms = (fromRole.length ? fromRole : fallback).filter(
        (f) => f?.isActive !== false,
      );

      const built: AllowedForm[] = [];
      const seen = new Set<string>();

      backendForms.forEach((bf) => {
        const nameN = normalize(bf.name);
        const keyN = normalize(bf.key);

        console.log("Nurse3 Form", {
          name: bf.name,
          key: bf.key,
          normalizedName: nameN,
          normalizedKey: keyN,
        });

        const isBUSampleForm =
          nameN.includes("B&U SAMPLE") ||
          keyN.includes("B&U SAMPLE") ||
          nameN.includes("BLOOD AND URINE SAMPLE") ||
          keyN.includes("BLOOD AND URINE SAMPLE") ||
          nameN.includes("BLOOD AND URIN SAMPLE") ||
          keyN.includes("BLOOD AND URIN SAMPLE");

        if (isBUSampleForm && !canCollectBUSample) {
          return;
        }

        const renderer =
          FORM_COMPONENTS[nameN] ?? FORM_COMPONENTS[keyN] ?? null;
        if (renderer) {
          const label = bf.name || bf.key || "Form";
          const uniqueId = bf._id || `${label}-${keyN || nameN}`;
          if (!seen.has(uniqueId)) {
            seen.add(uniqueId);
            built.push({ id: uniqueId, label, render: renderer });
          }
        }
      });

      // setVisibleForms(built);
      // setActiveIdx((prev) => (built[prev] ? prev : 0));

      // Keep the existing backend form order,
      // but always move the B&U Sample form to the last tab.
      const isBUSampleLabel = (label: string) => {
        const normalized = normalize(label);

        return (
          normalized.includes("B&U SAMPLE") ||
          normalized.includes("BLOOD AND URINE SAMPLE") ||
          normalized.includes("BLOOD AND URIN SAMPLE")
        );
      };

      const orderedForms = [...built].sort((a, b) => {
        const aIsBU = isBUSampleLabel(a.label);
        const bIsBU = isBUSampleLabel(b.label);

        if (aIsBU && !bIsBU) return 1;
        if (!aIsBU && bIsBU) return -1;

        return 0;
      });

      setVisibleForms(orderedForms);
      setActiveIdx((prev) => (orderedForms[prev] ? prev : 0));

      if (built.length === 0) {
        setTimeout(() => alert("No forms are assigned to your role."), 0);
      }
    } catch (err: any) {
      setLoadError(err.message || "Could not load permitted forms.");
      setVisibleForms([]);
      setActiveIdx(0);
    } finally {
      setLoadingForms(false);
    }
  }, [canCollectBUSample]);

  const validateBeforeComplete = () => {
    return true;
  };

  return (
    <section className="bnu-main">
      <Nurse3ActivePatientCard
        onPatientSelect={(patient) => console.log("Selected:", patient)}
        showActions={true}
        validateBeforeComplete={validateBeforeComplete}
        formStatus={formStatus}
        onStationStarted={() => setIsStarted(true)}
      />

      <div className="nurse2-subtabs" role="tablist">
        {loadingForms && <div className="nurse2-subtab disabled">Loading…</div>}
        {loadError && <div className="nurse2-subtab disabled">{loadError}</div>}
        {!loadingForms &&
          !loadError &&
          visibleForms.length > 0 &&
          visibleForms.map((form, i) => (
            <button
              key={form.id}
              type="button"
              className={`nurse2-subtab${activeIdx === i ? " active" : ""}`}
              onClick={() => setActiveIdx(i)}
            >
              {form.label}
            </button>
          ))}
        {!loadingForms && !loadError && visibleForms.length === 0 && (
          <div className="nurse2-subtab disabled">No forms assigned</div>
        )}
      </div>

      {/* Render selected form dynamically */}
      {activePatient &&
        isStarted &&
        !loadingForms &&
        !loadError &&
        visibleForms[activeIdx] &&
        React.cloneElement(visibleForms[activeIdx].render(emrId))}
    </section>
  );
};
